import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { formatWithThousandSeparator } from '@/utils';

interface AdvanceLowBalanceAlertProps {
  /** net value of the merchant's open scheduled-not-collected pickup */
  scheduledNet: number;
  /** remaining eligible balance */
  available: number;
}

/**
 * Conditional low-balance alert (DESIGN State A §2) — amber card, warning
 * triangle. Interpolates the scheduled-pickup net value and the remaining
 * balance. Each amount is wrapped in Unicode isolate chars (FSI…PDI, the
 * `<bdi>` equivalent) so an RTL sentence can never re-order the numerals or
 * the parentheses around them (DESIGN §6 bidi rule).
 */
function AdvanceLowBalanceAlert({ scheduledNet, available }: AdvanceLowBalanceAlertProps) {
  const { t } = useTranslation();
  // FSI (U+2068) … PDI (U+2069) — the character-level `<bdi>` equivalent,
  // safe to embed inside an i18next-interpolated string.
  const iso = (value: number) =>
    `⁨${formatWithThousandSeparator(value)} ${String(t('iqd'))}⁩`;

  return (
    <div
      className='mb-3.5 flex items-start gap-3 rounded-xl border border-solid border-[#F1DFAF] bg-[#FCF3DD] px-[18px] py-3.5'
      data-testid='advance-cod-low-balance-alert'
    >
      <AlertTriangle className='mt-px h-[19px] w-[19px] shrink-0 text-[#B7791F]' strokeWidth={2} />
      <p className='m-0 text-[13.5px] leading-[1.5] text-[#7A5A16]'>
        <b className='text-[#8A5A0E]'>{String(t('advance_cod_low_balance_title'))}</b>{' '}
        {String(
          t('advance_cod_low_balance_body', {
            scheduledNet: iso(scheduledNet),
            available: iso(available),
          }),
        )}
      </p>
    </div>
  );
}

export default AdvanceLowBalanceAlert;
